import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { allIndustriesData } from '../../data/allIndustriesData';
import { CompleteIndustryData } from '../../data/industryData';
import IndustryBottlenecks from './IndustryBottlenecks';
import TechStackSection from './TechStackSection';
import WhyChooseBetaque from './WhyChooseBetaque';
import '../../styles/IndustryDetailPage.css';

// Additional components for various industry-specific sections
import MLOutcomes from './MLOutcomes';
import CaseStudies from './CaseStudies';
import SupportStages from './SupportStages';
import InfraFeatures from './InfraFeatures';

const IndustryDetailPage: React.FC = () => {
  const { industryId } = useParams<{ industryId: string }>();
  const industry = allIndustriesData[industryId as keyof typeof allIndustriesData] as CompleteIndustryData;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!industry) {
    return (
      <div className="industry-not-found">
        <h1>Industry Not Found</h1>
        <p>Sorry, we couldn't find the industry you're looking for.</p>
        <Link to="/industries">View All Industries</Link>
      </div>
    );
  }

  return (
    <div className="ind-industry-detail-page">
      {/* Enhanced Breadcrumb */}
      <div className="betaque-service-breadcrumb">
        <Link to="/">Home</Link> / <Link to="/industries">Industries</Link> / {industry.hero.title}
      </div>

      {/* Modern Hero Section */}
      <div className="betaque-service-hero">
        <div className="betaque-service-hero-content">
          <div className="betaque-service-hero-left">
            <div className="betaque-service-meta">
              <span className="betaque-service-icon-large">{industry.hero.icon}</span>
              <span className="betaque-service-category-large">Industry</span>
            </div>
            
            <h1 className="betaque-service-title">{industry.hero.title}</h1>
            <h2 className="betaque-service-subtitle">{industry.hero.subtitle}</h2>
            <p className="betaque-service-description">{industry.hero.description}</p>
            
            {industry.hero.highlights && (
              <div className="betaque-service-highlights">
                {industry.hero.highlights.map((highlight, index) => (
                  <div key={index} className="betaque-highlight-item">
                    <span className="betaque-highlight-icon">✓</span>
                    <span>{highlight}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="betaque-service-cta-buttons">
              <Link to="/contact" className="betaque-primary-cta-btn">
                Get Started Today
              </Link>
              <Link to="/contact" className="betaque-secondary-cta-btn">
                Schedule Consultation
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Bottlenecks Section - if available */}
      {industry.components.bottleneckSections && (
        <IndustryBottlenecks sections={industry.components.bottleneckSections} />
      )}

      {/* Why Choose Betaque Section */}
      <WhyChooseBetaque 
        industryName={industry.hero.title}
        description={industry.hero.description}
        benefits={industry.benefits}
      />

      {/* Tech Stack Section - if available */}
      {industry.components.techStackItems && (
        <TechStackSection 
          title={`Our ${industry.hero.title} Engineering Stack`} 
          description={`Betaque ensures your ${industry.hero.title} infrastructure isn't just ready but optimized for performance, scale, and innovation.`}
          items={industry.components.techStackItems} 
        />
      )}

      {/* Render ML Outcomes if available */}
      {industry.components.mlOutcomes && (
        <MLOutcomes outcomes={industry.components.mlOutcomes} />
      )}

      {/* Render Case Studies if available */}
      {industry.components.caseStudies && (
        <CaseStudies caseStudies={industry.components.caseStudies} />
      )}

      {/* Render Support Stages if available */}
      {industry.components.supportStages && (
        <SupportStages stages={industry.components.supportStages} />
      )}

      {/* Render Infra Features if available */}
      {industry.components.infraFeatures && (
        <InfraFeatures features={industry.components.infraFeatures} />
      )}
    </div>
  );
};

export default IndustryDetailPage; 